<template>
  <div
    v-if="isBusy"
    class="row">
    <div class="col-12">
      <h5 data-cy="application-form-section-misc">
        {{ $t('message.applicationForm.miscInfo') }}
      </h5>
    </div>
    <!-- Busy -->
    <div
      class="col text-center">
      <coozzy-spinner />
    </div>
  </div>
  <div
    v-else
    class="row">
    <div class="col-12">
      <h5 data-cy="application-form-section-misc">
        {{ $t('message.applicationForm.miscInfo') }}
      </h5>
    </div>
    <div
      v-if="object && !object.acceptsCreditTrust && object.category !== 'PARKING_SPACE' && object.category !== 'SECONDARY_ROOM'"
      class="col-12 col-md-6">
      <label for="foreclosure">{{ $t('message.applicationForm.foreclosure') }} {{ (sending ? '*' : '' ) }}</label>
      <coozzy-form-select
        id="foreclosure"
        v-model="foreclosureLastTwoYears"
        data-cy="application-form-individual-foreclosure-select"
        :state="object && object.category !== 'PARKING_SPACE' && object.category !== 'SECONDARY_ROOM' && $v.foreclosureLastTwoYears.$dirty && $v.foreclosureLastTwoYears.$error ? false : null">
        <option value="">
          -
        </option>
        <option value="APPLIES">
          {{ $t('message.generic.selectField.yes') }}
        </option>
        <option value="DOES_NOT_APPLY">
          {{ $t('message.generic.selectField.no') }}
        </option>
      </coozzy-form-select>
    </div>
    <div
      v-if="object && !object.acceptsCreditTrust && object.category !== 'PARKING_SPACE' && object.category !== 'SECONDARY_ROOM'"
      class="col-12 col-md-6">
      <label for="debtCertificate">{{ $t('message.applicationForm.debtCertificate') }} {{ (sending ? '*' : '' ) }}</label>
      <coozzy-form-select
        id="debtCertificate"
        v-model="debtCertificateLastFiveYears"
        data-cy="application-form-individual-debt-select"
        :state="object && object.category !== 'PARKING_SPACE' && object.category !== 'SECONDARY_ROOM' && $v.debtCertificateLastFiveYears.$dirty && $v.debtCertificateLastFiveYears.$error ? false : null">
        <option value="">
          -
        </option>
        <option value="APPLIES">
          {{ $t('message.generic.selectField.yes') }}
        </option>
        <option value="DOES_NOT_APPLY">
          {{ $t('message.generic.selectField.no') }}
        </option>
      </coozzy-form-select>
    </div>
    <div class="col-12 col-md-6">
      <label for="liabilityInsurance">{{ $t('message.applicationForm.liabilityInsurance') }}</label>
      <coozzy-form-select
        id="liabilityInsurance"
        v-model="hasLiabilityInsurance"
        data-cy="application-form-individual-liability-insurance-select">
        <option value="UNDEFINED_VALUE">
          -
        </option>
        <option value="APPLIES">
          {{ $t('message.generic.selectField.yes') }}
        </option>
        <option value="DOES_NOT_APPLY">
          {{ $t('message.generic.selectField.no') }}
        </option>
      </coozzy-form-select>
    </div>
    <div
      v-if="isMainIndividual && object.category !== 'PARKING_SPACE' && object.category !== 'SECONDARY_ROOM'"
      class="col-12 col-md-6">
      <coozzy-form-input-numeric
        id="adults"
        v-model="adults"
        data-cy="application-form-individual-adults-input"
        :name="$t('message.applicationForm.adultCount')"
        :is-integer="true"
        type="number" />
    </div>
    <div
      v-if="isMainIndividual && object.category !== 'PARKING_SPACE' && object.category !== 'SECONDARY_ROOM'"
      class="col-12 col-md-6">
      <coozzy-form-input-numeric
        id="children"
        v-model="children"
        data-cy="application-form-individual-children-input"
        :name="$t('message.applicationForm.childrenCount')"
        :is-integer="true"
        type="number" />
    </div>
    <div
      v-if="parkingOptions.length > 0"
      class="col-12 col-md-12 mt-3">
      <label>
        {{ $t('message.applicationForm.desiredParkingOptions') }}
      </label>
      <div class="row m-0">
        <template v-for="(parkingTypeValue, parkingTypeIndex) in parkingOptions">
          <div
            :key="parkingTypeIndex"
            class="col-12">
            <div class="row">
              <coozzy-form-checkbox
                v-model="parkingTypeValue.checked"
                data-cy="application-form-individual-parking-option-checkbox"
                :initial="parkingTypeValue.checked"
                :class="'col-12 col-md-6 col-lg-3 d-flex align-items-center'"
                @change="parkingOptionChanged">
                <span>{{ parkingTypeValue.type === 'UNDEFINED_PARKING_TYPE' ? $t('message.onBoarding.buildings.objects.parkingSpaces.title') : $t('message.onBoarding.buildings.objects.parkingSpaces.parkingType.' + parkingTypeValue.type) }}</span><span class="ml-1">(CHF {{ parkingTypeValue.costs }})</span>
              </coozzy-form-checkbox>
              <coozzy-form-input
                v-if="parkingTypeValue.checked"
                :id="'licensePlate_' + parkingTypeIndex"
                v-model="parkingTypeValue.licensePlate"
                data-cy="application-form-individual-parking-license-plate-input"
                type="text"
                :class="'col-12 col-md-6 col-lg-3'"
                :name="$t('message.applicationForm.licensePlate')"
                @input="parkingOptionChanged" />
            </div>
          </div>
        </template>
      </div>
    </div>
    <div class="col-12 col-md-12 mt-3">
      <coozzy-form-textarea
        ref="remark"
        v-model="remark"
        data-cy="application-form-individual-remark-textarea"
        :initial="remark"
        :value-text-area="remark"
        name="remark"
        :rows="2">
        {{ $t('message.applicationForm.remark') }}
      </coozzy-form-textarea>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import { mapGetters, mapMutations } from 'vuex'
import CoozzyFormSelect from '@/framework/components/form/select/CoozzyFormSelect.vue'
import { validation } from '@/mixins/validation'
import { required } from 'vuelidate/lib/validators'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner.vue'
import CoozzyFormInputNumeric from '@/framework/components/form/input/CoozzyFormInputNumeric.vue'
import * as Sentry from '@sentry/vue'
import CoozzyFormCheckbox from '@/framework/components/form/checkbox/CoozzyFormCheckbox.vue'
import CoozzyFormInput from '@/framework/components/form/input/CoozzyFormInput.vue'
import CoozzyFormTextarea from '@/framework/components/form/textarea/CoozzyFormTextarea.vue'
import { V2RequestedParkingSpace } from '@/types/api'
import {
  ApplicationFormIndividual,
  ApplicationFormObjectMiscContext
} from '@/types/models'

interface ParkingOptionUi {
  type: string
  costs: number
  checked: boolean
  licensePlate: string
}

function isUndefinedValue(value: string) {
  return value !== 'UNDEFINED_VALUE'
}

export default Vue.extend({
  name: 'ApplicationFormIndividualMisc',
  components: {
    CoozzyFormTextarea,
    CoozzyFormInput,
    CoozzyFormCheckbox,
    CoozzyFormSelect,
    CoozzySpinner,
    CoozzyFormInputNumeric
  },
  mixins: [validation],
  props: {
    individualId: {
      type: String,
      required: true
    },
    sending: {
      type: Boolean,
      default: false
    },
    isBusy: {
      type: Boolean,
      default: false
    },
    isMainIndividual: {
      type: Boolean,
      default: false
    },
    object: {
      type: Object as PropType<ApplicationFormObjectMiscContext | null>,
      default: null
    }
  },
  data() {
    return {
      parkingOptions: [] as ParkingOptionUi[]
    }
  },
  computed: {
    ...mapGetters('applicationForm', ['getIndividualByInternalId']),
    individual(): ApplicationFormIndividual {
      return this.getIndividualByInternalId(this.individualId) as ApplicationFormIndividual
    },
    foreclosureLastTwoYears: {
      get(): string {
        return this.individual.legal.foreclosureLastTwoYears
      },
      set(value: string) {
        const ind = this.individual
        ind.legal.foreclosureLastTwoYears = value
        this.updateIndividual(ind)
      }
    },
    debtCertificateLastFiveYears: {
      get(): string {
        return this.individual.legal.debtCertificateLastFiveYears
      },
      set(value: string) {
        const ind = this.individual
        ind.legal.debtCertificateLastFiveYears = value
        this.updateIndividual(ind)
      }
    },
    hasLiabilityInsurance: {
      get(): string {
        return this.individual.insurance.hasLiabilityInsurance
      },
      set(value: string) {
        const ind = this.individual
        ind.insurance.hasLiabilityInsurance = value
        this.updateIndividual(ind)
      }
    },
    adults: {
      get(): number {
        return this.individual.household.adults
      },
      set(value: number) {
        const ind = this.individual
        ind.household.adults = value
        this.updateIndividual(ind)
      }
    },
    children: {
      get(): number {
        return this.individual.household.children
      },
      set(value: number) {
        const ind = this.individual
        ind.household.children = value
        this.updateIndividual(ind)
      }
    },
    parkingSpacesRequested: {
      get(): number {
        return this.individual.household.parkingSpacesRequested
      },
      set(value: number) {
        const ind = this.individual
        ind.household.parkingSpacesRequested = value
        this.updateIndividual(ind)
      }
    },
    remark: {
      get(): string {
        return this.individual.remark
      },
      set(value: string) {
        const ind = this.individual
        ind.remark = value
        this.updateIndividual(ind)
      }
    }
  },
  mounted() {
    if (this.object?.parking && this.object.parking.length > 0) {
      this.object.parking.forEach(parking => {
        this.parkingOptions.push({
          type: parking.parkingType,
          costs: parking.costs,
          checked: false,
          licensePlate: ''
        })
      })
    }
  },
  methods: {
    ...mapMutations('applicationForm', ['updateIndividual']),
    parkingOptionChanged() {
      const ind = this.individual
      ind.household.requestedParkingSpaces = this
        .parkingOptions as unknown as V2RequestedParkingSpace[]
      this.updateIndividual(ind)
    },
    isInvalid() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        Sentry.setExtra('individualMiscValidation-' + this.individualId, this.$v)
        this.scrollToError()
        return true
      }
      return false
    }
  },
  validations() {
    if (
      this.object &&
      this.object.category !== 'PARKING_SPACE' &&
      this.object.category !== 'SECONDARY_ROOM' &&
      !this.object.acceptsCreditTrust
    ) {
      return {
        foreclosureLastTwoYears: {
          required,
          isUndefinedValue
        },
        debtCertificateLastFiveYears: {
          required,
          isUndefinedValue
        }
      }
    }
    return {}
  }
})
</script>

<style lang="scss" scoped>
</style>
